<template>
  <content-section class="form-section mt-12 pt-12 border-t-2">
    <template #title>
      <h2>Staff Details</h2>
      <div class="italic text-sm text-grey-700">
        <p>
          Please provider with all the staff and their gross salaries. We will
          use this information to calculate their day and hourly rate based on:
          <br /><span class="text-xs inline-block p-2 rounded bg-grey-50 border border-grey-200"><code><span class="text-primary-500">day_rate</span> = <span class="text-neutral-700">gross_employee_cost</span> / 225</code> <br />
          <code><span class="text-secondary-600">hourly_rate</span> = <span class="text-primary-500">day_rate</span> / 8</code></span>
          <br />If you are not operating PAYE, the maximum claimable hourly rate is <b>£22p/h</b>, which equates to a salary of <b>£39,600 p/a</b>. Please ensure that all other fields are set to 0.
        </p>
        <p class="text-negative-500">
          <icon
            icon="important"
            class="w-4 h-4 icons-negative mr-1 align-text-bottom"
          />
          <strong
            >Please be sure that you enter this information correctly before
            submitting your contract</strong
          >
        </p>
      </div>
    </template>

    <template #cardless>
      <div style="display: grid; grid-auto-rows: 1fr;">
        <div
          v-for="(staffMember, index) in staff"
          :key="index"
          class="bg-white md:flex mb-2 pt-4 shadow"
        >
          <span
            class="bg-primary-500 text-primary-100 font-bold -mt-4 w-full md:w-6 flex items-center justify-center"
            :class="{
              'bg-primary-500 text-primary-100': valid(index),
              'bg-neutral-500 text-neutral-800': !valid(index)
            }"
          >
            {{ index + 1 }}
          </span>
          <div class="py-6 md:py-0 flex flex-wrap w-full">
            <input-default
              :error="getError('name', index)"
              :input="schema.name"
              :application-value="staffMember.name"
              @update:input="staffMember.name = $event.value"
            />
            <input-default
              :error="getError('job_title', index)"
              :input="schema.job_title"
              :application-value="staffMember.job_title"
              @update:input="staffMember.job_title = $event.value"
            />
            <div
              class="w-2/3 border-t pt-3 -mt-3"
              style="display: grid; grid-template-columns: 1fr 1fr; gap: 0.5rem;"
            >
              <input-default
                :error="getError('salary', index)"
                :input="schema.salary"
                :application-value="staffMember.salary"
                @update:input="staffMember.salary = parseFloat($event.value)"
              />
              <input-default
                :error="getError('employer_nic', index)"
                :input="schema.employer_nic"
                :application-value="staffMember.employer_nic"
                @update:input="staffMember.employer_nic = parseFloat($event.value)"
              />
              <input-default
                :error="getError('employer_pension', index)"
                :input="schema.employer_pension"
                :application-value="staffMember.employer_pension"
                @update:input="staffMember.employer_pension = parseFloat($event.value)"
              />
              <input-default
                :error="getError('medical_health_premiums', index)"
                :input="schema.medical_health_premiums"
                :application-value="staffMember.medical_health_premiums"
                @update:input="staffMember.medical_health_premiums = parseFloat($event.value)"
              />
              <input-default
                :error="getError('life_assurance_premiums', index)"
                :input="schema.life_assurance_premiums"
                :application-value="staffMember.life_assurance_premiums"
                @update:input="staffMember.life_assurance_premiums = parseFloat($event.value)"
              />
            </div>
            <div
              class="w-1/3 bg-grey-100 p-6 rounded -mt-3 border-t"
              style="display: grid; gap: 0.5rem;"
            >
              <input-default
                :input="schema.gross_employee_cost"
                :application-value="grossEmployeeCost(staffMember).toFixed(2)"
              />
              <input-default
                :input="schema.day_rate"
                :application-value="(grossEmployeeCost(staffMember) / 225).toFixed(2)"
              />
              <input-default
                :input="schema.hourly_rate"
                :application-value="(grossEmployeeCost(staffMember) / 225 / 8).toFixed(2)"
              />
            </div>
          </div>
          <button
            class="text-white flex items-center justify-center w-full h-6 md:w-6 md:h-auto self-stretch leading-none -mt-4"
            :class="{
              'bg-negative-600': !disableDelete,
              'cursor-not-allowed bg-grey-600': disableDelete
            }"
            :disabled="disableDelete"
            @click="deleteStaffMember(index)"
          >
            &times;
          </button>
        </div>
        <button
          class="bg-primary-50 uppercase text-primary-900 font-bold flex items-center transition-all transition-normal hover:bg-primary-100 w-full h-full justify-center border-2 border-dashed border-primary-300 rounded"
          @click="addStaff"
        >
          <icon icon="add" class="w-4 h-4 mr-2 icons-primary" />
          Add Staff Member
        </button>
      </div>
    </template>
  </content-section>
</template>

<script>
export default {
  name: "StaffDetails",
  props: {
    errors: {
      type: Object,
      default: () => ({})
    },
    staff: {
      type: Array,
      default: () => []
    },
    project: {
      type: Object,
      required: true
    },
    loading: {
      type: Boolean,
      required: true
    }
  },
  computed: {
    disableDelete() {
      return this.staff.length === 1;
    },
    schema() {
      return {
        name: {
          key: "name",
          type: "text",
          label: "Name",
          instructions: null,
          attributes: {
            placeholder: "Jane Doe",
            default: null,
            prepend: null,
            append: null,
            locked: this.loading,
            required: true
          },
          wrapper: {
            width: "1/2"
          },
          validation: {
            min: false,
            max: false,
            limit: false
          }
        },
        job_title: {
          key: "job_title",
          type: "text",
          label: "Job Title",
          instructions: null,
          attributes: {
            placeholder: "Project Manager",
            default: null,
            prepend: null,
            append: null,
            locked: this.loading,
            required: false
          },
          wrapper: {
            width: "1/2"
          },
          validation: {
            min: false,
            max: false,
            limit: false
          }
        },
        salary: {
          key: "salary",
          type: "number",
          label: "Salary",
          instructions: null,
          attributes: {
            placeholder: "45000.00",
            default: null,
            append: null,
            locked: this.loading,
            required: true,
            prepend: "£"
          },
          wrapper: {
            width: "full"
          },
          validation: {
            min: 0,
            max: 100000,
            step: 0.01,
            limit: false
          }
        },
        employer_nic: {
          key: "employer_nic",
          type: "number",
          label: "Employer NIC",
          instructions: null,
          attributes: {
            placeholder: "6500.00",
            default: null,
            append: null,
            locked: this.loading,
            required: true,
            prepend: "£"
          },
          wrapper: {
            width: "full"
          },
          validation: {
            min: 0,
            max: 100000,
            step: 0.01,
            limit: false
          }
        },
        employer_pension: {
          key: "employer_pension",
          type: "number",
          label: "Annual Employer Pension Contribution",
          instructions: null,
          attributes: {
            placeholder: "4500.00",
            default: null,
            append: null,
            locked: this.loading,
            required: true,
            prepend: "£"
          },
          wrapper: {
            width: "full"
          },
          validation: {
            min: 0,
            max: 100000,
            step: 0.01,
            limit: false
          }
        },
        medical_health_premiums: {
          key: "medical_health_premiums",
          type: "number",
          label: "Annual Medical/Health Insurance Premiums",
          instructions: null,
          attributes: {
            placeholder: "2500.00",
            default: 0,
            append: null,
            locked: this.loading,
            required: true,
            prepend: "£"
          },
          wrapper: {
            width: "full"
          },
          validation: {
            min: 0,
            max: 100000,
            step: 0.01,
            limit: false
          }
        },
        life_assurance_premiums: {
          key: "life_assurance_premiums",
          type: "number",
          label: "Annual Life Assurance Premiums",
          instructions: null,
          attributes: {
            placeholder: "2500.00",
            default: 0,
            append: null,
            locked: this.loading,
            required: true,
            prepend: "£"
          },
          wrapper: {
            width: "full"
          },
          validation: {
            min: 0,
            max: 100000,
            step: 0.01,
            limit: false
          }
        },
        gross_employee_cost: {
          key: "gross_employee_cost",
          type: "number",
          label: "Gross Employee Cost",
          instructions: null,
          attributes: {
            placeholder: "55000.00",
            default: null,
            append: null,
            locked: true,
            required: false,
            prepend: "£"
          },
          wrapper: {
            width: "full"
          },
          validation: {
            min: 0,
            max: 500000,
            step: 0.01,
            limit: false
          }
        },
        hourly_rate: {
          key: "hourly_rate",
          type: "number",
          label: "Hourly Rate",
          instructions: null,
          attributes: {
            placeholder: "45.00",
            default: null,
            append: null,
            locked: true,
            required: false,
            prepend: "£"
          },
          wrapper: {
            width: "full"
          },
          validation: {
            min: 0,
            max: 100000,
            step: 0.01,
            limit: false
          }
        },
        day_rate: {
          key: "day_rate",
          type: "number",
          label: "Day Rate",
          instructions: null,
          attributes: {
            placeholder: "450.00",
            default: null,
            append: null,
            locked: true,
            required: false,
            prepend: "£"
          },
          wrapper: {
            width: "full"
          },
          validation: {
            min: 0,
            max: 100000,
            step: 0.01,
            limit: false
          }
        }
      };
    }
  },
  methods: {
    grossEmployeeCost(staffMember) {
      return (
        staffMember.salary +
        staffMember.employer_nic +
        staffMember.employer_pension +
        staffMember.medical_health_premiums +
        staffMember.life_assurance_premiums
      );
    },
    valid(index) {
      const staffMember = this.staff[index];
      return (
        staffMember.name &&
        staffMember.name.length > 2 &&
        staffMember.job_title &&
        staffMember.job_title.length > 2 &&
        staffMember.salary &&
        staffMember.salary > 0 &&
        staffMember.employer_nic &&
        staffMember.employer_nic >= 0 &&
        staffMember.employer_pension &&
        staffMember.employer_pension >= 0 &&
        staffMember.medical_health_premiums &&
        staffMember.medical_health_premiums >= 0 &&
        staffMember.life_assurance_premiums &&
        staffMember.life_assurance_premiums >= 0
      );
    },
    addStaff() {
      this.staff.push({
        name: "",
        job_title: "",
        salary: null,
        employer_nic: null,
        employer_pension: null,
        medical_health_premiums: null,
        life_assurance_premiums: null
      });
    },
    deleteStaffMember(index) {
      if (this.disableDelete) return;
      if (this.valid(index)) {
        if (
          !confirm(
            "Are you sure you want to delete this staff member? This action cannot be undone."
          )
        ) {
          return;
        }
      }
      this.staff.splice(index, 1);
    },
    getError(field, index) {
      return this.errors[`staff.${index}.${field}`];
    }
  }
};
</script>
